import { IEventService } from '../../../services/eventService';
import { IAuthProviderService } from '../../../services/authProviderService';
import { TenantsDataRepository } from '../TenantDataRepository';
import { DEFAULT_PAGINATION } from './constants';
import * as T from './types';
import EventNames from '../../../config/eventNames';

/**
 * Base abstract class for all Tenant Handler implementations.
 * Provides common functionality for tenant management, caching, and pagination.
 *
 * This class is platform-agnostic and can be extended by different implementations
 * (Stratus, OneCloud, etc.)
 */
export default abstract class BaseTenantHandler {
  protected _paginatedTenantListPromisePool: Map<
    string,
    Promise<T.PaginatedTenantVisualizationType>
  >;
  protected _authProvider: IAuthProviderService;
  protected _stack: T.StackType;
  protected _eventService?: IEventService;
  protected _tenantsDataRepository?: TenantsDataRepository;

  TENANT_DATA_REPOSITORY_SUFFIX: string = '';

  constructor({
    stack,
    authProvider,
    tenantsDataRepository,
    eventService
  }: {
    stack: T.StackType;
    authProvider: IAuthProviderService;
    tenantsDataRepository?: TenantsDataRepository;
    eventService?: IEventService;
  }) {
    this._stack = stack;
    this._authProvider = authProvider;
    this._paginatedTenantListPromisePool = new Map();
    this._tenantsDataRepository = tenantsDataRepository;
    this._eventService = eventService;
  }

  /**
   * Retrieves a tenant by its ID.
   * First checks cache, then falls back to fetching from API.
   */
  public async getTenantById(
    tenantId: string
  ): Promise<T.TenantVisualizationType> {
    let tenant = this.findCachedTenantById(tenantId);

    if (!tenant) {
      const _options: T.GetTenantListOptionsType = {
        page: 0,
        tenantId,
        refresh: true
      };

      const paginatedTenantList = await this.getPaginatedTenantList(_options);

      tenant = paginatedTenantList?.tenants?.find((t) => t?.id === tenantId);
    }

    return tenant;
  }

  /**
   * Returns cached tenant list without making API calls.
   */
  public getTenantListFromCache(): T.TenantVisualizationType[] {
    return Object.values(
      this._tenantsDataRepository?.findOne(
        this.TENANT_DATA_REPOSITORY_SUFFIX
      ) || {}
    );
  }

  /**
   * Gets the complete tenant list with optional refresh.
   * Uses cache when available, fetches all pages when cache is empty.
   */
  public async getTenantList(
    refresh?: boolean
  ): Promise<T.TenantVisualizationType[]> {
    const tenantsCachedLists = refresh
      ? []
      : Object.values(
          this._tenantsDataRepository?.findOne(
            this.TENANT_DATA_REPOSITORY_SUFFIX
          ) || {}
        );

    let tenantList: T.TenantVisualizationType[];

    if (!tenantsCachedLists?.length) {
      // No cache, fetch all paginated results
      tenantList = await this.getAllPaginatedTenantList(true);

      void this._eventService?.publish(
        EventNames.shellTenantListUpdatedEventName,
        {
          tenantList
        }
      );
    } else {
      // Has cache, return cached tenants
      tenantList = tenantsCachedLists;
    }

    return tenantList;
  }

  /**
   * Fetches all pages of tenant data and consolidates into single list.
   * Handles pagination automatically and updates cache.
   */
  private async getAllPaginatedTenantList(
    refresh: boolean
  ): Promise<T.TenantVisualizationType[]> {
    const initialPage = 0;
    const tenantList: T.TenantVisualizationType[] = [];

    const { totalPages, tenants } = await this.getPaginatedTenantList({
      page: initialPage,
      refresh: true
    });

    // Accumulate tenants from first page
    tenantList.push(...tenants);

    // Check if there are more pages to fetch
    const hasMorePages = totalPages > 1;
    if (hasMorePages) {
      const promises = [];

      // Fetch remaining pages in parallel
      for (let page = 1; page < totalPages; page++) {
        promises.push(
          this.getPaginatedTenantList({
            page,
            refresh
          })
        );
      }
      const pagesResults =
        await Promise.all<T.PaginatedTenantVisualizationType>(promises);
      pagesResults.forEach(({ tenants: paginatedTenants }) => {
        tenantList.push(...paginatedTenants);
      });
    }

    const tenantsToCache = {};
    tenantList.forEach((tenant) => {
      tenantsToCache[tenant.id] = tenant;
    });

    this._tenantsDataRepository?.save(this.TENANT_DATA_REPOSITORY_SUFFIX, {
      ...tenantsToCache
    });

    return tenantList;
  }

  /**
   * Gets a single page of tenant data with intelligent caching and deduplication.
   * Properly respects the refresh parameter.
   */
  public async getPaginatedTenantList(
    options: T.GetPaginatedTenantListOptionsType
  ): Promise<T.PaginatedTenantVisualizationType> {
    const paginatedTenantListPromiseKey = JSON.stringify(options);

    const resultPromise = async () => {
      let result: T.PaginatedTenantVisualizationType;

      const cachedTenants =
        this._tenantsDataRepository?.findOne(
          this.TENANT_DATA_REPOSITORY_SUFFIX
        ) || {};

      const cachedTenantsList: T.TenantVisualizationType[] =
        Object?.values(cachedTenants);

      // Check cache only if not forced refresh
      if (!options?.refresh) {
        if (cachedTenantsList?.length) {
          result = {
            tenants: cachedTenantsList,
            currentPage: DEFAULT_PAGINATION.CURRENT_PAGE,
            totalPages: DEFAULT_PAGINATION.TOTAL_PAGES
          };
        }
        if (result) {
          return result;
        }
      }

      try {
        result = await this._getPaginatedTenantListData(options);
        return result;
      } finally {
        // Always cleanup the promise from pool, even on error
        this._paginatedTenantListPromisePool.delete(
          paginatedTenantListPromiseKey
        );
      }
    };

    const startedPromise = this._paginatedTenantListPromisePool?.get?.(
      paginatedTenantListPromiseKey
    );

    if (startedPromise) {
      return startedPromise;
    }

    const calledPromise = resultPromise();
    this._paginatedTenantListPromisePool.set(
      paginatedTenantListPromiseKey,
      calledPromise
    );
    return calledPromise;
  }

  /**
   * Helper method to merge lists and remove duplicates based on JSON serialization.
   */
  protected mergeListsAndRemoveDuplicates<T>(
    firstList: T[],
    secondList: T[]
  ): T[] {
    const uniqueSet = new Set(
      [...firstList, ...secondList].map((obj) => JSON.stringify(obj))
    );

    const uniqueList = Array.from(uniqueSet).map((objString) => {
      try {
        return JSON.parse(objString);
      } catch (err) {
        console.error(
          '@clientos-commons-northbound-api - BaseTenantHandler - mergeListsAndRemoveDuplicates error: ',
          err?.message ?? err
        );

        return undefined;
      }
    });

    return uniqueList.filter((item): item is T => item !== undefined);
  }

  /**
   * Helper method to find tenant from cached lists by tenant id.
   */
  protected findCachedTenantById(
    tenantId: string
  ): T.TenantVisualizationType | undefined {
    return this._tenantsDataRepository?.findOne(
      this.TENANT_DATA_REPOSITORY_SUFFIX
    )?.[tenantId];
  }

  /**
   * Abstract method to be implemented by concrete classes.
   * Defines how to fetch tenant data from specific API implementations.
   */
  public abstract _getPaginatedTenantListData(
    options: T.GetPaginatedTenantListOptionsType
  ): Promise<T.PaginatedTenantVisualizationType>;

  /**
   * Returns the authentication provider for this handler.
   */
  public getAuthProvider(): IAuthProviderService {
    return this._authProvider;
  }
}
